import uniqBy from 'lodash/uniqBy';

import { ServiceActionKeys } from '@ply-io/ply-front-shared-utilities';

import { ACTIONS_PATH_PREFIX } from './AppAction.const';
import AppService from './AppService';
import { END_RUN_ACTION_TYPES } from '@/components/BlockEditorWidget/FieldSettings/EndRunButtonsFieldSettings/end-run-field.const.js';

// Private
const getSourceOfParam = (param) => param.source;
const buildFetchKey = (...args) => args.join('-');
const _SERVICE_ACTION_COLLECTION_FETCH_KEY_PREFIX = 'sac';

// Public
const getRequiredParamsOfAction = (action) => (action?.params || []).filter((p) => p?.required);
const getSourceActionOfParam = (param) => getSourceOfParam(param).split('.')[0];
const getFieldKeyOfSourceActionOfParam = (param) => getSourceOfParam(param).split('.')[1];

/**
 * app is the related ServiceActionCollection if exists, if not same as originalApp
 * originalApp is always provided separately from the main app to support action specific icon
 */
const getActionIcon = ({ action, app, originalApp }) => {
	if (action?.has_icon) {
		const appSlug = action.service_slug || originalApp?.service_slug;
		const appVersion = action.service_version || originalApp?.version;
		return `${ACTIONS_PATH_PREFIX}/${appSlug}_${appVersion}_${action.key}`;
	} else if (app?.isServiceActionCollection) {
		return AppService.getServiceActionCollectionIconName(app.service_slug);
	}
	return originalApp?.service_slug;
};

const isAllowedForElementContext = (action, element) =>
	action.allowed_placements.length > 0 ? action.allowed_placements.includes(element?.placement) : true;

const doesElementHaveContextualAction = (actions, element) =>
	actions.some((action) => action.allowed_placements.length > 0 && action.allowed_placements.includes(element?.placement));

const isActionOfBranchRoot = (action) => action?.key === ServiceActionKeys.RunBranch;
const isActionOfConsolidateData = (action) => action?.key === ServiceActionKeys.ConsolidateData;
const isActionOfLoopStart = (action) => action?.key === ServiceActionKeys.LoopStart;
const isActionOfLoopEnd = (action) => action?.key === ServiceActionKeys.LoopEnd;
const isActionOfMiniApp = (action) => action?.key === ServiceActionKeys.RunMiniApp;
const isSendNotification = (action) => action?.key === ServiceActionKeys.SendNotification;
const isActionOfEndRunWidget = (action) => action?.key === END_RUN_ACTION_TYPES.DISPLAY_INTERFACE;
const isActionRestrictedBy = (action, type) => action?.restricted_by_type === type;
const isActionCreditBased = (action) => action?.is_credit_based;
const isActionRestricted = (action) => !!action?.restricted_by_type;
const isActionOfSubFlow = (action) => action?.key === ServiceActionKeys.SubFlow;

const buildActionsFetchKey = (serviceId, serviceVersionId) => buildFetchKey(serviceId, serviceVersionId); // sample use (serviceId, serviceVersionId) => '[service-id]-[service-version-id]'
const buildServiceActionCollectionActionsFetchKey = (serviceActionCollectionId) =>
	buildFetchKey(_SERVICE_ACTION_COLLECTION_FETCH_KEY_PREFIX, serviceActionCollectionId);

const findParamFromAction = (action, paramKey) => action.params.find((param) => param.key === paramKey);
const getActionsCurrentActionDependsOn = (action, getAction, includeCurrent = true) => {
	const requiredParamsOfAction = AppActionService.getRequiredParamsOfAction(action);
	const actionsOfRequiredParams = requiredParamsOfAction.map((requiredParam) =>
		getActionsCurrentActionDependsOn(getAction(AppActionService.getSourceActionOfParam(requiredParam)), getAction)
	);
	const dependsOn = (includeCurrent ? [action] : []).concat(...actionsOfRequiredParams);

	// filter out duplicate actions
	const ids = dependsOn.map((a) => a.id);
	const distinctDependsOn = dependsOn.filter(({ id }, index) => !ids.includes(id, index + 1));

	return distinctDependsOn;
};

const getRequiredActionParamsFromParamDependsOn = (param, action, getActionByKey, includeCurrent = true) => {
	const requiredActionParams = includeCurrent ? [param] : [];
	const paramDependsOnParams = (includeCurrent ? [param] : []).concat(
		(getDependsOnOfActionParam(param) || []).map((dependsOnParamKey) => findParamFromAction(action, dependsOnParamKey))
	);
	const requiredActions = paramDependsOnParams.map((dependsOnParam) => getActionByKey(getSourceActionOfParam(dependsOnParam)));
	for (const requiredAction of requiredActions) {
		const requiredParamsOfAction = getRequiredParamsOfAction(requiredAction);
		requiredActionParams.push(...requiredParamsOfAction);
		requiredActionParams.push(
			...requiredParamsOfAction
				.map((requiredParamOfAction) => getRequiredActionParamsFromParamDependsOn(requiredParamOfAction, requiredAction, getActionByKey))
				.flat()
		);
	}

	return uniqBy(requiredActionParams, 'key');
};

const getDependsOnOfActionParam = (param) => param.depends_on;

const doesActionHasServiceActionCollection = (action) => !!getActionServiceActionCollection(action);
const getActionServiceActionCollection = (action) => action?.service_action_collection_id;

const AppActionService = {
	isActionOfSubFlow,
	isActionOfMiniApp,
	isActionOfLoopEnd,
	isActionOfLoopStart,
	isActionOfEndRunWidget,
	isActionRestricted,
	isActionRestrictedBy,
	isActionCreditBased,
	isSendNotification,
	findParamFromAction,
	buildActionsFetchKey,
	buildServiceActionCollectionActionsFetchKey,
	getSourceActionOfParam,
	getRequiredParamsOfAction,
	getDependsOnOfActionParam,
	isActionOfBranchRoot,
	isActionOfConsolidateData,
	isAllowedForElementContext,
	doesElementHaveContextualAction,
	getActionsCurrentActionDependsOn,
	getFieldKeyOfSourceActionOfParam,
	getRequiredActionParamsFromParamDependsOn,
	getActionIcon,
	doesActionHasServiceActionCollection,
	getActionServiceActionCollection,
};

export default AppActionService;
